import React from 'react'
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import PainterNotificationsPanel from './PainterNotificationsPanel'
import DayStrip from './PainterScheduleView/DayStrip'
import EventList from './PainterScheduleView/EventList'
import ScheduleHeader from './PainterScheduleView/ScheduleHeader'
import WeekNavigator from './PainterScheduleView/WeekNavigator'
import PainterAccessProhibited from '../PainterSchedulePage/PainterAccessProhibited'
import type { PainterScheduleViewProps } from './PainterScheduleView/types'
import { usePainterAdditionalWorkStyles } from './PainterScheduleView/usePainterAdditionalWorkStyles'
import { usePainterJobCardStyles } from './PainterScheduleView/usePainterJobCardStyles'
import { usePainterScheduleLayoutStyles } from './PainterScheduleView/usePainterScheduleLayoutStyles'
import { usePainterWorkOrderStyles } from './PainterScheduleView/usePainterWorkOrderStyles'
import { usePainterScheduleView } from './usePainterScheduleView'

export default function PainterScheduleView(props: PainterScheduleViewProps) {
  const { events = [], showHours = true, loading, accessProhibited } = props
  const layoutClasses = usePainterScheduleLayoutStyles()
  const jobClasses = usePainterJobCardStyles()
  const workClasses = usePainterWorkOrderStyles()
  const additionalWorkClasses = usePainterAdditionalWorkStyles()

  const {
    isDesktop,
    weekStart,
    weekOffset,
    weekDays,
    daysWithEvents,
    selectedDay,
    setSelectedDay,
    expandedJobId,
    setExpandedJobId,
    activeTabs,
    setActiveTabs,
    expandedCrewNotes,
    setExpandedCrewNotes,
    expandedProducts,
    setExpandedProducts,
    setMenuOpen,
    notificationsOpen,
    setNotificationsOpen,
    showContactInfo,
    showTasksNotesFiles,
    canAddAdditionalWork,
    showPrices,
    showPayments,
    shiftWeek,
    toggleSet,
    callbacks
  } = usePainterScheduleView(props)

  return (
    <div className={layoutClasses.root}>
      <ScheduleHeader
        userAvatarUrl={props.userAvatarUrl}
        onMenuClick={() => props.onMenuClick?.() ?? setMenuOpen((open) => !open)}
        classes={layoutClasses}
      />
      <WeekNavigator
        weekStart={weekStart}
        weekOffset={weekOffset}
        onPrevWeek={() => shiftWeek(-1)}
        onNextWeek={() => shiftWeek(1)}
        disabled={accessProhibited}
        classes={layoutClasses}
      />
      <DayStrip
        weekDays={weekDays}
        selectedDay={selectedDay}
        daysWithEvents={daysWithEvents}
        onSelectDay={setSelectedDay}
        classes={layoutClasses}
      />
      {accessProhibited ? (
        <PainterAccessProhibited />
      ) : (
        <EventList
          events={events}
          loading={loading}
          selectedDay={selectedDay}
          expandedJobId={expandedJobId}
          activeTabs={activeTabs}
          expandedCrewNotes={expandedCrewNotes}
          expandedProducts={expandedProducts}
          showContactInfo={showContactInfo}
          showHours={showHours}
          showPrices={showPrices}
          showPayments={showPayments}
          showTasksNotesFiles={showTasksNotesFiles}
          canAddAdditionalWork={canAddAdditionalWork}
          onSetExpandedJobId={setExpandedJobId}
          onSetTab={(jobId, tab) => setActiveTabs((prev) => ({ ...prev, [jobId]: tab }))}
          onToggleCrewNote={(jobId) => toggleSet(setExpandedCrewNotes, jobId)}
          onToggleProducts={(jobId) => toggleSet(setExpandedProducts, jobId)}
          callbacks={callbacks}
          classes={jobClasses}
          workClasses={workClasses}
          additionalWorkClasses={additionalWorkClasses}
        />
      )}
      {notificationsOpen && isDesktop ? (
        <Dialog open onClose={() => setNotificationsOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle
            rightContent={
              <IconButton size="small" onClick={() => setNotificationsOpen(false)}>
                <CloseIcon />
              </IconButton>
            }
          >
            <Typography variant="h3">Notifications</Typography>
          </DialogTitle>
          <DialogContent>
            <PainterNotificationsPanel
              userAvatarUrl={props.userAvatarUrl}
              prefs={props.notificationPrefs}
              showTasksNotesFiles={showTasksNotesFiles}
              hideHeader
              onChange={props.onNotificationPrefsChange}
            />
          </DialogContent>
        </Dialog>
      ) : notificationsOpen ? (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1300, backgroundColor: '#fff' }}>
          <PainterNotificationsPanel
            userAvatarUrl={props.userAvatarUrl}
            prefs={props.notificationPrefs}
            showTasksNotesFiles={showTasksNotesFiles}
            onMenuClick={() => {
              setNotificationsOpen(false)
              setMenuOpen(true)
            }}
            onChange={props.onNotificationPrefsChange}
          />
        </div>
      ) : null}
    </div>
  )
}

export type {
  PainterAdditionalWork,
  PainterContact,
  PainterFile,
  PainterMediaItem,
  PainterNote,
  PainterNotificationPrefs,
  PainterPayment,
  PainterScheduleEvent,
  PainterScheduleViewProps,
  PainterTask,
  PainterWorkAreaSummary,
  PainterWorkItem,
  PainterWorkSubarea
} from './PainterScheduleView/types'
